import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ParentPaymentDialog from './ParentPaymentDialog';

// Icons
import { 
  Users, 
  CreditCard, 
  Bell, 

  Calendar, 
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  GraduationCap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INVOICE_LABELS = { PENDING: 'À payer', PARTIAL: 'Partiellement payée', OVERDUE: 'En retard', PAID: 'Payée', CANCELLED: 'Annulée', SENT: 'À payer', DRAFT: 'Brouillon', PENDING_VALIDATION: 'Paiement en attente de validation' };
const PERIOD_LABELS = { MONTHLY: 'mensuelle', QUARTERLY: 'trimestrielle', ANNUAL: 'annuelle', YEARLY: 'annuelle', ONE_TIME: 'unique', BIANNUAL: 'semestrielle', SEMESTER: 'semestrielle' };

const ParentDashboard = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/parent/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching parent dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDialogOpen(true);
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      'DRAFT': 'badge-secondary',
      'SENT': 'badge-primary', 
      'UNPAID': 'badge-warning',
      'PAID': 'badge-success',
      'OVERDUE': 'badge-danger'
    };
    return colors[status] || 'badge-secondary';
  };

  const getInvoiceStatusIcon = (status) => {
    const icons = {
      'DRAFT': FileText,
      'SENT': Clock,
      'UNPAID': AlertTriangle, 
      'PAID': CheckCircle,
      'OVERDUE': AlertTriangle
    };
    return icons[status] || FileText;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-6">
        <Card className="card">
          <CardContent className="pt-6">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune donnée disponible</h3>
              <p className="text-gray-600">
                Impossible de charger les informations du tableau de bord.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { children, pending_invoices, recent_payments, recent_attendance = [], recent_grades = [], recent_sanctions = [], notifications_count, total_pending_amount, summary } = dashboardData;

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <User className="w-6 h-6 mr-2 text-blue-600" />
            Tableau de bord Parent
          </h1>
          <p className="text-gray-600 mt-1">
            Bienvenue {user.first_name}, voici le suivi de vos enfants
          </p>
        </div>

        {notifications_count > 0 && (
          <Button className="btn-primary">
            <Bell className="w-4 h-4 mr-2" />
            {notifications_count} notification{notifications_count > 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Users className="w-4 h-4 mr-2 text-blue-600" />
              Mes Enfants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{summary.total_children}</div>
            <p className="text-xs text-gray-600 mt-1">Enfants inscrits</p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <FileText className="w-4 h-4 mr-2 text-orange-600" />
              Factures en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{summary.pending_invoices_count}</div>
            <p className="text-xs text-gray-600 mt-1">À régler</p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <CreditCard className="w-4 h-4 mr-2 text-red-600" />
              Montant total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{total_pending_amount.toLocaleString('fr-FR')} GNF</div>
            <p className="text-xs text-gray-600 mt-1">À payer</p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <CreditCard className="w-4 h-4 mr-2 text-green-600" />
              Paiements récents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary.recent_payments_count}</div>
            <p className="text-xs text-gray-600 mt-1">Ce mois</p>
          </CardContent>
        </Card>
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="invoices">Factures</TabsTrigger>
          <TabsTrigger value="children">Mes enfants</TabsTrigger>
          <TabsTrigger value="attendance">Présences</TabsTrigger>
          <TabsTrigger value="performance">Notes & Sanctions</TabsTrigger>
          <TabsTrigger value="notifications">
            Notifications {notifications_count > 0 && `(${notifications_count})`}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="overview-children">
            {children.map((child) => (
              <Card key={child.id} className="card">
                <CardContent className="pt-6">
                  <p className="font-semibold">{child.user_info?.first_name} {child.user_info?.last_name}</p>
                  <p className="text-sm text-gray-600">Classe : {child.class_name || 'Non affecté'}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {pending_invoices.length === 0 && recent_payments.length === 0 && (
            <p className="text-sm text-gray-500" data-testid="overview-empty">Aucune facture en attente ni paiement récent.</p>
          )}
          {pending_invoices.length > 0 && (
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                  Factures urgentes
                </CardTitle>
                <CardDescription>
                  Factures arrivant à échéance prochainement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pending_invoices.slice(0, 3).map((invoice) => {
                    const StatusIcon = getInvoiceStatusIcon(invoice.status);
                    return (
                      <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <StatusIcon className="w-4 h-4 text-gray-600" />
                          <div>
                            <p className="font-medium">{invoice.title}</p>
                            <p className="text-sm text-gray-600">
                              Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge className={getInvoiceStatusColor(invoice.status)}>
                            {INVOICE_LABELS[invoice.pending_validation ? 'PENDING_VALIDATION' : (invoice.financial_status || invoice.status)] || invoice.status}
                          </Badge>
                          <span className="font-semibold">{(invoice.balance ?? invoice.total_amount).toLocaleString('fr-FR')} GNF</span>
                          {!invoice.pending_validation && <Button 
                            size="sm" 
                            onClick={() => handlePayInvoice(invoice)}
                            className="btn-primary"
                            data-testid={`parent-home-pay-${invoice.id}`}
                          >
                            Payer
                          </Button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Payments */}
          {recent_payments.length > 0 && (
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-green-600" />
                  Paiements récents ({recent_payments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recent_payments.map((payment) => (
                    <div key={payment.payment_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <div>
                          <p className="font-medium">Paiement #{payment.payment_id.slice(-8)}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="badge-success">Payé</Badge>
                        <span className="font-semibold">{payment.amount.toLocaleString('fr-FR')} GNF</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          {pending_invoices.length > 0 ? (
            <div className="space-y-4">
              {pending_invoices.map((invoice) => {
                const StatusIcon = getInvoiceStatusIcon(invoice.status);
                return (
                  <Card key={invoice.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <StatusIcon className="w-4 h-4 text-gray-600" />
                            <h3 className="font-semibold text-lg">{invoice.title}</h3>
                            <Badge className={getInvoiceStatusColor(invoice.status)}>
                              {INVOICE_LABELS[invoice.pending_validation ? 'PENDING_VALIDATION' : (invoice.financial_status || invoice.status)] || invoice.status}
                            </Badge>
                          </div>

                          {invoice.description && (
                            <p className="text-sm text-gray-700 mb-2">{invoice.description}</p>
                          )}

                          <div className="text-sm text-gray-600 space-y-1">
                            <p className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2" />
                              Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                            </p>
                            <p>Périodicité : {PERIOD_LABELS[invoice.periodicity] || invoice.periodicity}</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600 mb-2">
                            {(invoice.balance ?? invoice.total_amount).toLocaleString('fr-FR')} GNF
                          </div>
                          {invoice.pending_validation ? (
                            <p className="text-sm text-sky-700" data-testid={`parent-invoice-pending-${invoice.id}`}>Paiement déclaré, en attente de validation</p>
                          ) : (
                          <Button 
                            onClick={() => handlePayInvoice(invoice)}
                            className="btn-primary"
                            data-testid={`parent-invoice-pay-${invoice.id}`}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Payer maintenant
                          </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune facture en attente</h3>
              <p className="text-gray-600">
                Toutes les factures de vos enfants sont à jour.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Children Tab */}
        <TabsContent value="children" className="space-y-4">
          {children.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {children.map((child) => (
                <Card key={child.id} className="card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <User className="w-5 h-5 mr-2 text-blue-600" />
                      {child.user_info?.first_name} {child.user_info?.last_name}
                    </CardTitle>
                    <CardDescription>
                      N° étudiant: {child.student_number}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p>
                        <strong>Classe:</strong> {child.class_name || 'Non affecté'}
                      </p>
                      <p>
                        <strong>Date de naissance:</strong> {' '}
                        {new Date(child.birth_date).toLocaleDateString('fr-FR')}
                      </p>
                      <p>
                        <strong>Inscription:</strong> {' '}
                        {new Date(child.enrollment_date).toLocaleDateString('fr-FR')}
                      </p>
                      {child.medical_info && (
                        <p>
                          <strong>Info médicale:</strong> {child.medical_info}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun enfant enregistré</h3>
              <p className="text-gray-600">
                Contactez l'administration pour lier vos enfants à votre compte.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-4">
          {recent_attendance.length > 0 ? (
            <div className="space-y-4">
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                    Présences récentes (30 derniers jours)
                  </CardTitle>
                  <CardDescription>
                    Absences et retards de vos enfants
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recent_attendance.map((attendance) => {
                      const child = children.find(c => c.id === attendance.student_id);
                      const statusColor = attendance.status === 'ABSENT' ? 'text-red-600' : 'text-orange-600';
                      const statusBadge = attendance.status === 'ABSENT' ? 'badge-danger' : 'badge-warning';
                      
                      return (
                        <div key={attendance.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <AlertTriangle className={`w-4 h-4 ${statusColor}`} />
                            <div>
                              <p className="font-medium">
                                {child?.user_info?.first_name} {child?.user_info?.last_name}
                              </p>
                              <p className="text-sm text-gray-600">
                                {attendance.date ? new Date(attendance.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                                {attendance.subject && ` - ${attendance.subject}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Badge className={statusBadge}>
                              {attendance.status === 'ABSENT' ? 'Absent' : 'Retard'}
                            </Badge>
                            {attendance.reason && (
                              <p className="text-sm text-gray-600 max-w-xs truncate">
                                {attendance.reason}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 mx-auto text-green-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune absence récente</h3>
              <p className="text-gray-600">
                Vos enfants ont une excellente assiduité !
              </p>
            </div>
          )}
        </TabsContent>

        {/* Performance Tab (Grades & Sanctions) */}
        <TabsContent value="performance" className="space-y-4">
          {/* Grades Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="flex items-center">
                <GraduationCap className="w-5 h-5 mr-2 text-blue-600" />
                Notes récentes (90 derniers jours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent_grades.length > 0 ? (
                <div className="space-y-3">
                  {recent_grades.map((grade) => {
                    const child = children.find(c => c.id === grade.student_id);
                    const percentage = (grade.grade / grade.max_grade) * 100;
                    const gradeColor = percentage >= 70 ? 'text-green-600' : percentage >= 50 ? 'text-orange-600' : 'text-red-600';
                    
                    return (
                      <div key={grade.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">
                            {child?.user_info?.first_name} {child?.user_info?.last_name}
                          </p>
                          <p className="text-sm text-gray-600">{grade.assessment_title}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(grade.created_at).toLocaleDateString('fr-FR')} - {grade.assessment_type}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${gradeColor}`}>
                            {grade.grade} / {grade.max_grade}
                          </div>
                          <p className="text-xs text-gray-600">Coef: {grade.coefficient}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Aucune note récente</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sanctions Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                Sanctions récentes (90 derniers jours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent_sanctions.length > 0 ? (
                <div className="space-y-3">
                  {recent_sanctions.map((sanction) => {
                    const child = children.find(c => c.id === sanction.student_id);
                    const severityColor = sanction.severity === 'HIGH' ? 'badge-danger' : sanction.severity === 'MEDIUM' ? 'badge-warning' : 'badge-secondary';
                    
                    return (
                      <div key={sanction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <div className="flex-1">
                            <p className="font-medium">
                              {child?.user_info?.first_name} {child?.user_info?.last_name}
                            </p>
                            <p className="text-sm text-gray-700">{sanction.description}</p>
                            <p className="text-xs text-gray-500">
                              {sanction.date ? new Date(sanction.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={severityColor}>
                            {sanction.severity === 'HIGH' ? 'Grave' : sanction.severity === 'MEDIUM' ? 'Moyen' : 'Léger'}
                          </Badge>
                          <p className="text-xs text-gray-600 mt-1">{sanction.sanction_type}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-2" />
                  <p className="text-gray-600">Aucune sanction - Excellent comportement !</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card className="card">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="w-5 h-5 mr-2 text-blue-600" />
                Notifications ({notifications_count})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.notifications && dashboardData.notifications.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`p-4 rounded-lg border ${notification.is_read ? 'bg-white' : 'bg-blue-50 border-blue-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="font-semibold text-gray-900">{notification.title}</p>
                            {!notification.is_read && (
                              <Badge className="badge-primary text-xs">Nouveau</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-600">Aucune notification pour le moment</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog - Multi-Method (Orange Money, Cash, Chèque) */}
      <ParentPaymentDialog
        invoice={selectedInvoice}
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedInvoice(null);
        }}
        onPaymentSubmitted={() => {
          fetchDashboardData();
          toast.success('Paiement enregistré avec succès!');
        }}
      />
    </div>
  );
};

export default ParentDashboard;